import React from "react";
import { useMoveListElement } from "@planzone/view-models";
import { ProjectElementType } from "@planzone/dto";
import { DropBox as BaseDropBox } from "@common/dragAndDrop";
import { Preview } from "../preview";
import { Props } from "./types";

function getElTypeFromMoveType(moveType: string): ProjectElementType {
  return moveType === "MOVE_TASK"
    ? ProjectElementType.TASK
    : moveType === "MOVE_MILESTONE"
      ? ProjectElementType.MILESTONE
      : moveType === "MOVE_EVENT"
        ? ProjectElementType.EVENT
        : ProjectElementType.LIST;
}

function DropBox(props: Props) {
  const { isRequestPending: isMovePending, moveListElement } =
    useMoveListElement();

  return props.type === "dropStart" ? (
    <BaseDropBox
      style={props.style}
      acceptedTypes={["MOVE_TASK", "MOVE_MILESTONE", "MOVE_LIST", "MOVE_EVENT"]}
      verticalSlop={{ top: 35 }}
      drop={({ payload, type }) => {
        const { parentList, gantt } = props;

        !isMovePending &&
          moveListElement({
            currentParentList: payload.parentList,
            nextParentList: parentList,
            projectElementType: getElTypeFromMoveType(type),
            projectElementId: payload.projectElement.uuid,
            range: {
              min: null,
              max: gantt[0] || null,
            },
            alignDates: false,
          });
      }}
    >
      {({ payload }, canDrop) => (
        <Preview
          canDrop={canDrop}
          isList={payload && payload.type === "MOVE_LIST"}
        />
      )}
    </BaseDropBox>
  ) : props.type === "dropAtEl" ? (
    <BaseDropBox
      style={props.style}
      acceptedTypes={["MOVE_TASK", "MOVE_MILESTONE", "MOVE_LIST", "MOVE_EVENT"]}
      drop={({ type, payload }) => {
        const { parentList, gantt, projectElement, index } = props;

        !isMovePending &&
          moveListElement({
            currentParentList: payload.parentList,
            nextParentList: parentList,
            projectElementType: getElTypeFromMoveType(type),
            projectElementId: payload.projectElement.uuid,
            range: {
              min: projectElement,
              max: gantt[index + 1] || null,
            },
            alignDates: false,
          });
      }}
      isDisabled={({ payload }) =>
        payload.projectElement &&
        payload.projectElement.uuid === props.projectElement.uuid
      }
    >
      {props.children}
    </BaseDropBox>
  ) : props.type === "dropInList" ? (
    <BaseDropBox
      style={props.style}
      acceptedTypes={["MOVE_TASK", "MOVE_MILESTONE", "MOVE_LIST", "MOVE_EVENT"]}
      drop={({ type, payload }) => {
        const { parentList, gantt, projectElement, index } = props;

        !isMovePending &&
          moveListElement({
            currentParentList: payload.parentList,
            nextParentList: parentList,
            projectElementType: getElTypeFromMoveType(type),
            projectElementId: payload.projectElement.uuid,
            range: {
              min: null,
              max:
                gantt[index + 1] &&
                gantt[index + 1].parentUuid === projectElement.uuid
                  ? gantt[index + 1]
                  : null,
            },
            alignDates: false,
          });
      }}
      isDisabled={({ payload }) =>
        payload.projectElement &&
        payload.projectElement.uuid === props.projectElement.uuid
      }
    >
      {props.children}
    </BaseDropBox>
  ) : props.type === "dropAfterList" ? (
    <BaseDropBox
      style={props.style}
      verticalSlop={{ top: 15, bottom: 0 }}
      acceptedTypes={["MOVE_TASK", "MOVE_MILESTONE", "MOVE_LIST", "MOVE_EVENT"]}
      drop={({ payload, type }) => {
        const { gantt, nextParent, projectElement, index } = props;

        const nextElement =
          gantt[index + 1] && gantt[index + 1].depth === projectElement.depth
            ? {
                ...gantt[index + 1],
                rank: gantt[index + 1].referenceKeyRank,
              }
            : null;

        !isMovePending &&
          moveListElement({
            currentParentList: payload.parentList,
            nextParentList: nextParent,
            projectElementType: getElTypeFromMoveType(type),
            projectElementId: payload.projectElement.uuid,
            range: {
              min: projectElement,
              max: nextElement,
            },
            alignDates: false,
          });
      }}
    >
      {(_, canDrop) => <Preview canDrop={canDrop} />}
    </BaseDropBox>
  ) : (
    <React.Fragment />
  );
}

export { DropBox };
